import { useContext, useEffect, useState } from "react"
import UserContext from "../Contexts/UserContext";
import { default as axios } from "../axiosConfig";
import { API_CreateLearningJournalEntry, API_GetAdminViewJournalEntries, API_GetCandidateLearningJournals, API_GetCandidateTimesheetsForJournals, API_UpdateCandidateLearningJournal } from "../Assets/ApiUrlContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faPen } from "@fortawesome/free-solid-svg-icons";
import { ModalPrompt } from "./ModalPrompt";

const LearningJournals = () => {
    const currentUser = useContext(UserContext);
    const [blankEntryDates, setBlankEntryDates] = useState([]);
    const [journalRawEntries, setJournalRawEntries] = useState([]);
    const [allJournalEntries, setAllJournalEntries] = useState({});
    const [journedWeeks, setJournaledWeeks] = useState([]);
    const [isAdminView, setIsAdminView] = useState(false);
    const [modalToggleState, setModalToggleState] = useState(false);
    const [modalIsEditEntry, setModalIsEditEntry] = useState(false);
    const [skillsEntered, setSkillsEntered] = useState([]);
    const [skillEntryText, setSkillEntryText] = useState("");
    const [newSkillsLearned, setNewSkillsLearned] = useState(false);

    const [modalObjectState, setModalObjectState] = useState({
        entryId: 0,
        week: "",
        entryText: "",
        skillsLearned: ""
    });

    const [errorStates, setErrorStates] = useState({
        week: false,
        entryText: false,
        skillsLearned: true
    });

    useEffect(() => {
        if (currentUser.user.IsAdmin) {
            setIsAdminView(true);
            const getAllAdminViewJournalEntries = async () => {
                let allEntries = await axios.get(API_GetAdminViewJournalEntries);
                setJournalRawEntries(allEntries.data);
            }

            getAllAdminViewJournalEntries();
        } else {
            setIsAdminView(false);

            if (currentUser.user.CandidateId) {
                const getAllCandidateJournalEntries = async () => {
                    let allEntries = await axios.get(API_GetCandidateLearningJournals, {
                        params: { CandidateId: currentUser.user.CandidateId }
                    });

                    setJournalRawEntries(allEntries.data);
                }

                getAllCandidateJournalEntries();
            }
        }

    }, [currentUser.user]);

    useEffect(() => {
        let monthYearGroupings = {};
        let journaledWorkWeeks = [];

        journalRawEntries.forEach(entry => {
            let monthYearEntry = `${new Date(entry.TimesheetStartDate).toLocaleString('default', { month: 'long' })} ${new Date(entry.TimesheetStartDate).getFullYear()}`;
            if (!monthYearGroupings[monthYearEntry]) {
                monthYearGroupings[monthYearEntry] = [{
                    id: entry.Id,
                    candidateId: entry.CandidateId,
                    candidateName: entry.CandidateName || "",
                    jobId: entry.JobId,
                    timesheetId: entry.TimesheetId,
                    dateString: entry.DateString,
                    tsStartDate: entry.TimesheetStartDate,
                    journalEntry: entry.JournalEntry,
                    skillsLearned: entry.SkillsLearned
                }]
            } else {
                monthYearGroupings[monthYearEntry] = [
                    ...monthYearGroupings[monthYearEntry],
                    {
                        id: entry.Id,
                        candidateId: entry.CandidateId,
                        candidateName: entry.CandidateName || "",
                        jobId: entry.JobId,
                        timesheetId: entry.TimesheetId,
                        dateString: entry.DateString,
                        tsStartDate: entry.TimesheetStartDate,
                        journalEntry: entry.JournalEntry,
                        skillsLearned: entry.SkillsLearned
                    }
                ]
            }

            // set the list of weeks that were worked so that we can generate the list of weeks not worked for selection
            journaledWorkWeeks.push(entry.dateString);
        });

        setJournaledWeeks(journaledWorkWeeks);
        setAllJournalEntries(monthYearGroupings);
    }, [journalRawEntries])

    useEffect(() => {
        // get all distinct timesheet start dates
        if (currentUser.user.CandidateId) {
            const getAllPossibleJournalDates = async () => {
                let allCandidateTimesheets = await axios.get(API_GetCandidateTimesheetsForJournals, {
                    params: { CandidateId: currentUser.user.CandidateId }
                });

                let distinctWorkWeeks = [];
                allCandidateTimesheets.data.forEach(ts => {
                    let tsWeekStartDate = new Date(ts.TimesheetStartDate);
                    let tsWeekStartMonth = tsWeekStartDate.toLocaleString('default', { month: 'long' });
                    let tsWeekStartYear = tsWeekStartDate.getFullYear();

                    let tsWeekEndDate = new Date(ts.TimesheetEndDate);
                    let tsWeekEndMonth = tsWeekEndDate.toLocaleString('default', { month: 'long' });
                    let tsWeekEndYear = tsWeekEndDate.getFullYear();

                    let combinedString = `${ts.CompanyName}: ${tsWeekStartMonth} ${tsWeekStartDate.getDate()}${tsWeekStartYear === tsWeekEndYear ? "" : `, ${tsWeekStartYear} `} - ${tsWeekEndMonth} ${tsWeekEndDate.getDate()}, ${tsWeekEndYear}`;
                    if (!distinctWorkWeeks.includes(combinedString)) {
                        distinctWorkWeeks.push({
                            dateString: combinedString,
                            jobId: ts.JobId,
                            tsStartDate: tsWeekStartDate,
                            timesheetId: ts.TimesheetId
                        });
                    }
                });

                let sortedDistinctWorkWeeks = distinctWorkWeeks.sort((a, b) => b.tsStartDate - a.tsStartDate);

                // iterate over the journal entries already made and exclude those dates from the dropdown selection
                let unjournaledWorkWeeks = [];
                sortedDistinctWorkWeeks.forEach(workWeek => {
                    if (!journedWeeks.includes(workWeek.dateString)) {
                        unjournaledWorkWeeks.push(workWeek);
                    }
                });

                setBlankEntryDates(unjournaledWorkWeeks);
            }

            getAllPossibleJournalDates();
        }
    }, [currentUser.user, journedWeeks])

    const handlePenClick = (entry) => {
        setModalIsEditEntry(true);
        setSkillsEntered(entry.skillsLearned.length > 0 ? entry.skillsLearned.split(",") : []);
        setNewSkillsLearned(entry.skillsLearned.length > 0);
        setModalObjectState({
            entryId: entry.id,
            week: entry.dateString,
            entryText: entry.journalEntry,
            skillsLearned: entry.skillsLearned
        });

        setModalToggleState(true);
    };

    const handleAddJournalClick = () => {
        setModalIsEditEntry(false);
        setModalObjectState({
            week: "",
            entryText: "",
            skillsLearned: ""
        });

        setModalToggleState(true);
    };

    const handleModalSubmit = async () => {
        if (modalIsEditEntry) {
            let updatedJournal = await axios.put(API_UpdateCandidateLearningJournal, {
                Id: modalObjectState.entryId,
                JournalEntry: modalObjectState.entryText,
                SkillsLearned: modalObjectState.skillsLearned
            });

            if (updatedJournal.data) {
                setJournalRawEntries(journalRawEntries.map(entry => {
                    if (entry.id !== updatedJournal.data.id) return entry;
                    entry.JournalEntry = updatedJournal.data.JournalEntry;
                    entry.SkillsLearned = updatedJournal.data.SkillsLearned;
                    return entry;
                }))
            }

        } else {
            let workWeekDetails = blankEntryDates.find(entry => entry.dateString === modalObjectState.week);

            let newJournal = await axios.post(API_CreateLearningJournalEntry, {
                CandidateId: currentUser.user.CandidateId,
                JobId: workWeekDetails.jobId,
                TimesheetId: workWeekDetails.timesheetId,
                DateString: workWeekDetails.dateString,
                TimesheetStartDate: workWeekDetails.tsStartDate,
                JournalEntry: modalObjectState.entryText,
                SkillsLearned: modalObjectState.skillsLearned
            });

            if (newJournal.data) {
                let sortedRawEntries = [...journalRawEntries, newJournal.data].sort((a, b) => new Date(b.TimesheetStartDate) - new Date(a.TimesheetStartDate));
                setJournalRawEntries(sortedRawEntries);
            }
        }

        handleModalClose();
    }

    const handleModalClose = () => {
        setModalToggleState(false);
        setSkillsEntered([]);
        setSkillEntryText("");
        setErrorStates({ week: false, entryText: false, skillsLearned: false });
        setModalIsEditEntry(false);
        setNewSkillsLearned(false);
        setModalObjectState({ week: "", entryText: "", skillsLearned: "" });
    }

    const handleModalObjectUpdate = (entryName, entryUpdate) => {
        setModalObjectState({ ...modalObjectState, [entryName]: entryUpdate });
        setErrorStates({ ...errorStates, [entryName]: entryUpdate.length == 0 });
    }

    const handleRemoveSkillFromJournal = (skillName) => {
        let indexSkill = skillsEntered.findIndex(skill => skill.toLowerCase() === skillName.toLowerCase());
        let newSkillsList = [
            ...skillsEntered.slice(0, indexSkill),
            ...skillsEntered.slice(indexSkill + 1, skillsEntered.length)
        ];

        setSkillsEntered(newSkillsList);
        handleModalObjectUpdate("skillsLearned", newSkillsList.toString());
    };

    const handleSkillTextEntry = (event) => {
        setSkillEntryText(event.target.value);
    };

    const handleSkillEntryEnterKey = (event) => {
        if (event.key === "Enter") {
            addSkillToList();
            setSkillEntryText("");
        }
    };

    const addSkillToList = () => {
        if (!skillsEntered.some((skill) => skill.toLowerCase() === skillEntryText.trim().toLowerCase())) {
            let updatedList = [...skillsEntered, skillEntryText];
            setSkillsEntered(updatedList);
            handleModalObjectUpdate("skillsLearned", updatedList.toString());
        }
    };

    return (
        <>
            <div className="journalContainer">
                <div className="tgs-content">
                    <div className="journalHeading">
                        <div className="headingText">Learning Journals</div>
                        {!isAdminView && (
                            <div className="addButton">
                                <button onClick={handleAddJournalClick}>ADD JOURNAL ENTRY</button>
                            </div>
                        )}
                    </div>
                </div>

                {allJournalEntries && Object.entries(allJournalEntries).map(([key, value]) => (
                    <>
                        <div className="monthYear" key={key}>{key}</div>
                        {
                            value.map((entry) => (
                                <div className="tgs-content" key={`${entry.candidateId}-${entry.dateString}`}>
                                    {isAdminView && <div className="candidateName">{entry.candidateName}</div>}

                                    <div className={`dateStringContent ${isAdminView ? "namePaddingReduce" : ""}`}>
                                        <div className="dateText">{entry.dateString}</div>
                                        {!isAdminView && (
                                            <div className="editIcon">
                                                <span onClick={() => handlePenClick(entry)}>
                                                    <FontAwesomeIcon icon={faPen} />
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="journalText">{entry.journalEntry}</div>

                                    {entry.skillsLearned.length > 0 && (
                                        <>
                                            <div className="learnedSkills">
                                                <div className="title">Skills Learned:</div>
                                                <div className="skills">
                                                    {entry.skillsLearned.split(",").map(skill => (
                                                        <div className="skillEntryBubble" key={skill}><span>{skill}</span></div>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))
                        }
                    </>
                ))}
            </div>

            <ModalPrompt
                onModalClose={handleModalClose}
                modalToggleState={modalToggleState}
                modalTitle={
                    modalIsEditEntry ? 'Edit Journal Entry' : 'Create Journal Entry'
                }
                modalContent={(
                    <>
                        <div className="journalModal">
                            <div className="modalDescription">
                                {!modalIsEditEntry && <span>Select the week and share what you learned. Your journal entries help track progress and support your professional growth.</span>}
                                {modalIsEditEntry && <span>Edit and update what you learned. Update your journal entry to help maintain the complete progress and support your professional growth.</span>}
                            </div>

                            <div className="journalWeekSelection">
                                {!modalIsEditEntry && (
                                    <>
                                        <select
                                            className={`${errorStates.week ? "inputError" : ""}`}
                                            value={modalObjectState.week}
                                            onChange={(e) => handleModalObjectUpdate("week", e.target.value)}>
                                            <option value='' disabled={true}>Select Week</option>
                                            {blankEntryDates.map(blankJournalWeek => (
                                                <option key={blankJournalWeek.dateString}>{blankJournalWeek.dateString}</option>
                                            ))}
                                        </select>
                                        {errorStates.role && <div className="inputErrorMsg">This field is required</div>}
                                    </>
                                )}

                                {modalIsEditEntry && <input type="text" className="editDisabledJournalWeek" value={modalObjectState.week} disabled={true} />}
                            </div>

                            <div className="journalEntryText">
                                <textarea
                                    placeholder="Journal Entry"
                                    className="journalEntryTextField"
                                    value={modalObjectState.entryText}
                                    onChange={(e) => handleModalObjectUpdate("entryText", e.target.value)}
                                ></textarea>
                            </div>

                            <div className="journalNewSkillsLearned">
                                <div className="journalCheckBoxContainer">
                                    <input type="checkbox" id="journalCheckBox" name="journalCheckBox" checked={newSkillsLearned} onChange={(e) => setNewSkillsLearned(e.target.checked)} />
                                    <label htmlFor="journalCheckBox">I have learned new skills during this period</label>
                                </div>

                                {newSkillsLearned && (
                                    <>
                                        <div className="journalSkills">
                                            <div className="skillEntry">
                                                {skillsEntered.map(skill => (
                                                    <div className="skillEntryBubble" key={skill}>
                                                        <span>{skill}</span>
                                                        <FontAwesomeIcon icon={faCircleXmark} onClick={() => handleRemoveSkillFromJournal(skill)} />
                                                    </div>
                                                ))}
                                            </div>

                                            <input type="text" placeholder="Skill Name" className={`${errorStates.skillsLearned ? "inputError" : ""}`} value={skillEntryText} onInput={handleSkillTextEntry} onKeyDown={handleSkillEntryEnterKey} />
                                            {errorStates.skillsLearned && <div className="inputErrorMsg">This field is required</div>}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="modalBtns">
                                <button className="cancelBtn" onClick={handleModalClose}>CANCEL</button>
                                <button className="confirmBtn" onClick={handleModalSubmit} disabled={modalObjectState.week.length === 0 || modalObjectState.entryText.length === 0 || (newSkillsLearned && errorStates.skillsLearned)}>
                                    {modalIsEditEntry ? 'UPDATE' : 'SUBMIT'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            />
        </>
    )
}

export default LearningJournals